import {
	EWorkerDocumentType,
	type IWorkerDocumentBtpCard,
	type IWorkerDocumentDegree,
	type IWorkerDocumentHabilitation,
	type IWorkerDocumentOfficalDocuments,
} from '@/types/documents';

import type { IAddress } from '@/types/address';
import type { IAgencyDB } from '@/types/agency';
import type {
	DataSet,
	DB,
	LabelValue,
	ObjectID,
	Timestamp,
} from '@/types/common';
import type { ICompanyDB } from '@/types/company';
import type { EContractType } from '@/types/contract';
import type { IJobDB, JobExperience } from '@/types/job';
import type { IJobOfferDB, IJobOfferMission } from '@/types/jobOffer';
import type { IPerson } from '@/types/person';
import type { IInterimDB } from '@/types/worker';

export type MissionStatusOption = LabelValue<string | string[]>;

export enum MissionStatus {
	draft = 'draft', // super status: newCommand
	created = 'created', // super status: newCommand

	inPreparation = 'inPreparation', // super status: inProgressCommand
	proposed = 'proposed', // super status: inProgressCommand

	ready = 'ready', // super status: futureMission
	completed = 'completed', // super status: futureMission

	inProgress = 'inProgress', // super status: inProgressMission

	evaluation = 'evaluation', // super status: finishedMission
	terminated = 'terminated', // super status: finishedMission

	archived = 'archived',
	deleted = 'deleted',
	canceled = 'canceled',

	// Legacy
	presented = 'presented', // super status: inProgressCommand
	oldConfirmed = 'confirmed',
	oldTodo = 'todo',
	oldWaiting = 'waiting',
}

export const MissionStatusGroup = {
	unclosedStatus: [
		MissionStatus.draft,
		MissionStatus.created,
		MissionStatus.inPreparation,
		MissionStatus.presented,
		MissionStatus.proposed,
		MissionStatus.ready,
	],
	closedStatus: [
		MissionStatus.completed,
		MissionStatus.inProgress,
		MissionStatus.evaluation,
		MissionStatus.terminated,
		MissionStatus.archived,
	],
};

export enum WorkerMissionStatus {
	PROPOSED = 'PROPOSED',
	REPROPOSED = 'REPROPOSED',
	REPROPOSED_BY_SMS = 'REPROPOSED_BY_SMS',
	ACCEPTED = 'ACCEPTED',
	CONFIRMED = 'CONFIRMED',
	DECLINED = 'DECLINED',
	REJECTED_BY_AGENCY = 'REJECTED_BY_AGENCY',
	ABORTED = 'ABORTED',
	NOT_CONFIRMED = 'NOT_CONFIRMED',
}

export type MissionKanbanStatusInfo = {
	statusKanban: string;
	color: string;
	icon: string;
	iconColor: string;
};

export type WorkerMissionStatusInfo = {
	label: string;
	color: string;
	icon: string;
};

export enum MissionCommand {
	addInterim = 'addInterim',
	agencyDecline = 'agencyDecline',
	archive = 'archive',
	askAgency = 'askAgency',
	cancel = 'cancel',
	complete = 'complete',
	confirm = 'confirm',
	copy = 'copy',
	delete = 'delete',
	evaluate = 'evaluate',
	notConfirm = 'notConfirm',
	present = 'present',
	propose = 'propose',
	proposeToAll = 'proposeToAll',
	removeWorker = 'removeWorker',
	sendOrderToEvolia = 'sendOrderToEvolia',
	show = 'show',
	showSelection = 'showSelection',
	update = 'update',
	workerAbort = 'workerAbort',
	repropose = 'repropose',

	verifyConfirmWorker = 'verifyConfirmWorker',
	workerAccept = 'workerAccept',
	workerAgencyReject = 'workerAgencyReject',
	workerCompanyReject = 'workerCompanyReject',
	workerConfirm = 'workerConfirm',
	workerDecline = 'workerDecline',
	workerPresent = 'workerPresent',
	workerPropose = 'workerPropose',
	workerReproposed = 'workerReproposed',
	workerDetails = 'workerDetails',
	workerValidate = 'workerValidate',
	workerReproposedBySMS = 'workerReproposedBySMS',
}

export enum CancelReason {
	clientNeedNoLongerExists = 'clientNeedNoLongerExists',
	fasterCompetition = 'fasterCompetition',
	noAvailableWorker = 'noAvailableWorker',
	workerDidNotShowUp = 'workerDidNotShowUp',
	customerChanges = 'customerChanges',
	myNeedNoLongerExists = 'myNeedNoLongerExists',
	needTreatedByOther = 'needTreatedByOther',
	other = 'other',
}

export enum AlertLevel {
	info = 'info',
	todo = 'todo',
	warning = 'warning',
}

export enum AlertKind {
	missingWorker = 'missingWorker',
	missionNotCompleted = 'missionNotCompleted',
	missingEvaluation = 'missingEvaluation',
	workerAccepted = 'workerAccepted',
	workerAborted = 'workerAborted',
	workerDeclined = 'workerDeclined',
	workerValidated = 'workerValidated',
	workerRejectedByCompany = 'workerRejectedByCompany',
	workersToConfirmed = 'workersToConfirmed',
	companyAsk = 'companyAsk',
	companyAskQuote = 'companyAskQuote',
	addWorkers = 'addWorkers',
	replaceWorkers = 'replaceWorkers',
	selectAgency = 'selectAgency',
	workersToValidate = 'workersToValidate',
	workersToEvaluate = 'workersToEvaluate',
}

export type Alert = {
	kind: AlertKind;
	level: AlertLevel;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	data: any;
};

export type MissionOneEvaluation = {
	note: number;
	comment?: string;
	blacklisted?: boolean;
};

export type MissionEvaluation = {
	[key: string]: {
		worker?: MissionOneEvaluation;
		company?: MissionOneEvaluation;
	};
};

export type AgencyRejection = {
	agencyId: ObjectID;
	agencyAskDate: Timestamp;
	rejectionDate: Timestamp;
};

export type WorkerMission = {
	workerId: ObjectID;
	status: WorkerMissionStatus;
	positionIndex: number;
	firstname?: string;
	lastname?: string;
	periodIndex?: number;
	slot?: string;
	timeSlotIndex?: number;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	error?: any;
	availableCommands?: Array<MissionCommand>;
	rejectionReason?: string;
	rejectionTimestamp?: Timestamp;
	abortReason?: string;
	abortTimestamp?: Timestamp;
	propositionTimestamp?: Timestamp;
	presentationTimestamp?: Timestamp;
	presentationMessage?: string;
	responseTime?: Timestamp;
	confirmedTimestamp?: Timestamp;
	response?: number;
	registered?: boolean;
	phone?: string;
	email?: string;
};

export type TimeSlot = {
	from: number;
	to: number;
};

export type MissionPeriod = {
	start: Timestamp;
	end: Timestamp;
	weekendIncluded?: boolean;
	slots: DataSet<TimeSlot[]>;
};

export type MissionPosition = {
	jobId: ObjectID;
	job?: IJobDB;
	experience: JobExperience | null;
	quantity: number;
	description?: string;
	hourlyWage?: number;
	totalEstimatedWage?: number;
	filledPositions?: number;
	daysWorked?: string[];
	jobOfferId?: ObjectID;
	contractType: EContractType;
	justification?: string;
	hiringReason?: string;
};

export type MissionPositionFront = {
	job?: IJobDB;
	jobId?: ObjectID;
	jobName?: string;
	experience: JobExperience | null;
	quantity: number;
	description?: string;
	justification?: string;
	hiringReason?: string;
	workers?: IInterimDB[];
};

export interface IMission {
	name: string;
	nameSlug: string;
	agencyId?: ObjectID;
	ownerId: ObjectID;
	ownerCompany?: IPerson;
	ownerAgency?: IPerson;
	ownerInitialsCompany?: string;
	ownerInitialsAgency?: string;
	companyOwnerId?: ObjectID;
	clientId: ObjectID;
	status: MissionStatus;
	address?: IAddress;
	positions?: MissionPosition[];
	periods?: MissionPeriod[];
	description?: string;
	internalAgencyNotes?: string;
	autoConfirmation?: boolean;
	cancelReason?: CancelReason;
	cancelReasonOther?: string;
	cancelDate?: Timestamp;
	workers?: Array<WorkerMission>;
	evaluation?: MissionEvaluation;
	searchedJobs?: ObjectID[];
	companyAskTimestamp?: Timestamp;
	evaluationTimestamp?: Timestamp;
	completionTimestamp?: Timestamp;
	rejections?: Array<AgencyRejection>;
	presentationToken?: string;
	completionWorkersCount?: number;
	lastRead?: Timestamp;
	alerts?: Array<Alert>;
	extendsOf?: ObjectID;
	presentationMode?: boolean;
	viewed?: boolean;
	startAt?: Timestamp;
	endAt?: Timestamp;
	jobOfferId?: ObjectID;
	sendToEvoliaTimestamp?: Timestamp;
	jobOffers?: IJobOfferMission[];
	orderId?: number;
	orderRef?: number;
	client?: ICompanyDB;
	agency?: IAgencyDB;
	owner?: IPerson;
	companyOwner?: IPerson;
	job?: IJobDB;
	jobOffer?: IJobOfferDB;
}

export interface IMissionDB extends IMission, DB {}

interface MissionJobOffer extends IJobOfferDB {
	workerToTreated: number;
}

export interface IMissionList extends IMissionDB {
	name: string;
	status: MissionStatus;
	clientId: string;
	agencyId: string;
	createdAt: number;
	ownerAgency: {
		firstname: string;
		lastname: string;
		state?: string;
	};
	address: IAddress;
	positions: (MissionPosition & {
		job: IJobDB;
		experienceLevel: number;
		workerDocuments: {
			[EWorkerDocumentType.BTP_CARD]: IWorkerDocumentBtpCard[];
			[EWorkerDocumentType.HABILITATION]: IWorkerDocumentHabilitation[];
			[EWorkerDocumentType.OFFICIAL_DOCUMENTS]: IWorkerDocumentOfficalDocuments[];
			[EWorkerDocumentType.DEGREE]: IWorkerDocumentDegree[];
		};
	})[];
	workers: Array<
		Pick<
			WorkerMission,
			| 'firstname'
			| 'lastname'
			| 'status'
			| 'registered'
			| 'workerId'
			| 'positionIndex'
			| 'confirmedTimestamp'
		>
	>;
	endAt: number;
	periods: MissionPeriod[];
	startAt: number;
	availableCommands: MissionCommand[];
	extendsOf?: string;
	sendToEvoliaTimestamp: number;
	jobOffer: MissionJobOffer;
	client: ICompanyDB;
	internalAgencyNotes?: string;
	description?: string;
	jobOfferId?: string;
	totalQuantity?: number;
	confirmedWorkersCount?: number;
	workerConfirmed?: number;
}
